import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { marketing } from '@lunarcrush/ds/tokens';
import { Code, Plugs, Terminal, Compass, SquaresFour, BookOpen, Football } from '@phosphor-icons/react';

interface MegaMenuItem {
  key: string;
  route: string;
  color: string;
  icon: 'claude' | 'chatgpt' | 'api' | 'mcp' | 'cli' | 'discover' | 'collections' | 'docs' | 'fantasy';
  /** True when `route` leaves the marketing app. The docs live in the webapp,
   *  a separate build on the same host, so the router cannot handle it and the
   *  browser has to do a real navigation. */
  external?: boolean;
  /** Renders a shared SOON badge next to the label (DEV-12167 Fantasy Football,
   *  not yet launched — same badge as the pricing "LunarCrush Apps" row). */
  soon?: boolean;
}

// Single muted slate for every product icon. This is the hex value of
// marketing.textMuted, frozen so the `${color}18`-style alpha math below stays
// valid (a CSS var would break it). Mid-slate reads ~4:1 on both the light and
// dark menu panels, and using one color keeps the menu consistent with the
// product grid (which can't theme-shift its accent for the same alpha reason).
const MUTED = '#6B7090';

// DEV-12165: order here IS the visible Platform nav order — this grid replaces
// the DS dropdown (hidden below), so the ticket's "Discover, API, MCP Server,
// CLI, Collections, + Claude, + ChatGPT (reading top-left across)" is enforced
// by THIS array, not the CMS `navigation` order (which only drives the mobile
// drawer + no-JS fallback). Keep the two in sync.
const MENU_ITEMS: MegaMenuItem[] = [
  { key: 'discover', route: '/products/lunarcrush-discover', color: MUTED, icon: 'discover' },
  { key: 'api', route: '/products/lunarcrush-api', color: MUTED, icon: 'api' },
  { key: 'mcp', route: '/products/lunarcrush-mcpserver', color: MUTED, icon: 'mcp' },
  { key: 'cli', route: '/products/lunarcrush-cli', color: MUTED, icon: 'cli' },
  { key: 'collections', route: '/products/lunarcrush-custom-collections', color: MUTED, icon: 'collections' },
  { key: 'claude', route: '/products/lunarcrush-claude', color: MUTED, icon: 'claude' },
  { key: 'chatgpt', route: '/products/lunarcrush-chatgpt', color: MUTED, icon: 'chatgpt' },
  { key: 'fantasy', route: '/products/fantasy-football', color: MUTED, icon: 'fantasy' },
];

const ICON_MAP: Record<MegaMenuItem['icon'], React.FC<{ color: string; size: number }>> = {
  claude: ({ size }) => (
    <img src="/landing-assets/claude-logo.svg" alt="Claude" style={{ width: size, height: size, objectFit: 'contain' }} />
  ),
  // DEV-12165 r5: real OpenAI mark (chatgpt-logo.svg), mirroring the Claude tile.
  // Both branded tiles are now <img>; the other five stay Phosphor icons.
  chatgpt: ({ size }) => (
    <img src="/landing-assets/chatgpt-logo.svg" alt="ChatGPT" style={{ width: size, height: size, objectFit: 'contain' }} />
  ),
  api: ({ color, size }) => <Code size={size} weight="duotone" color={color} />,
  mcp: ({ color, size }) => <Plugs size={size} weight="duotone" color={color} />,
  cli: ({ color, size }) => <Terminal size={size} weight="duotone" color={color} />,
  discover: ({ color, size }) => <Compass size={size} weight="duotone" color={color} />,
  collections: ({ color, size }) => <SquaresFour size={size} weight="duotone" color={color} />,
  docs: ({ color, size }) => <BookOpen size={size} weight="duotone" color={color} />,
  fantasy: ({ color, size }) => <Football size={size} weight="duotone" color={color} />,
};

const ItemIcon: React.FC<{ icon: MegaMenuItem['icon']; color: string }> = ({ icon, color }) => {
  const IconComponent = ICON_MAP[icon];
  return (
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 8,
        background: `${color}18`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <IconComponent color={color} size={18} />
    </div>
  );
};

/**
 * Find the Platform nav item rendered by the DS Navbar.
 * Walks up from `anchor` to the `.sticky-nav` or `.secondary-hero-nav` wrapper,
 * then finds the element whose text matches the (possibly translated) label.
 */
function findPlatformNavItem(anchor: HTMLElement, label: string): HTMLElement | null {
  const nav = anchor.closest('.sticky-nav, .secondary-hero-nav');
  if (!nav) return null;
  // The DS Navbar renders each NavItem inside a div[style*="position: relative"]
  const candidates = nav.querySelectorAll<HTMLElement>('div[style*="relative"]');
  for (const el of candidates) {
    const text = el.firstElementChild?.textContent?.trim();
    if (text?.startsWith(label)) return el;
  }
  return null;
}

export const PlatformMegaMenu: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const platformElRef = useRef<HTMLElement | null>(null);
  const platformLabel = t('nav.platform');

  const clearTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const handleOpen = useCallback(() => {
    clearTimer();
    setOpen(true);
  }, [clearTimer]);

  const handleStartClose = useCallback(() => {
    clearTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  }, [clearTimer]);

  // Attach listeners to the DS Platform nav item + hide its default dropdown.
  // Re-runs when the translated label changes so locale switches re-anchor.
  useEffect(() => {
    if (!anchorRef.current) return;
    const el = findPlatformNavItem(anchorRef.current, platformLabel);
    if (!el) return;
    platformElRef.current = el;

    // Hide the DS default dropdown via a style tag
    const dsDropdown = el.querySelector<HTMLElement>('div[style*="position: absolute"]');
    if (dsDropdown) {
      (dsDropdown as HTMLElement).style.display = 'none';
    }

    el.addEventListener('mouseenter', handleOpen);
    el.addEventListener('mouseleave', handleStartClose);

    return () => {
      el.removeEventListener('mouseenter', handleOpen);
      el.removeEventListener('mouseleave', handleStartClose);
    };
  });

  // Keep DS dropdown hidden on every render (it may re-render)
  useEffect(() => {
    if (!platformElRef.current) return;
    const dsDropdown = platformElRef.current.querySelector<HTMLElement>('div[style*="position: absolute"]');
    if (dsDropdown) {
      dsDropdown.style.display = 'none';
    }
  });

  return (
    <div ref={anchorRef} className="mega-menu-anchor">
      <div
        className={`mega-menu-panel ${open ? 'mega-menu-open' : ''}`}
        onMouseEnter={handleOpen}
        onMouseLeave={handleStartClose}
      >
        <div className="mega-menu-inner">
          <div className="mega-menu-grid">
            {MENU_ITEMS.map((item) => {
              const isHovered = hoveredItem === item.key;
              const shortLabel = t(`megaMenu.${item.key}.short`);
              const description = t(`megaMenu.${item.key}.description`);
              return (
                // DEV-10940: a real <a>, not a div with a handler. These were
                // divs, so the product pages had no crawlable path from the nav
                // and could not be opened in a new tab. Internal routes still
                // navigate client-side via preventDefault; the docs live in a
                // separate app, so that one is left to the browser.
                <a
                  key={item.key}
                  href={item.route}
                  className="mega-menu-item"
                  style={{
                    background: isHovered ? `${item.color}08` : 'transparent',
                    borderColor: isHovered ? `${item.color}30` : 'transparent',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={() => setHoveredItem(item.key)}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={(e) => {
                    if (item.external || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                    e.preventDefault();
                    navigate(item.route);
                    setOpen(false);
                  }}
                >
                  <ItemIcon icon={item.icon} color={item.color} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 13,
                        fontWeight: 600,
                        /* Hover feedback comes from the tinted bg/border below;
                           keep the title on the theme-aware heading token. The
                           old `brand.white` hover was a no-op in dark (heading is
                           already white) but went invisible on the light panel. */
                        color: marketing.textHeading,
                        marginBottom: 2,
                        transition: 'color 200ms ease',
                      }}
                    >
                      {shortLabel}
                    </div>
                    <div
                      style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 11.5,
                        color: marketing.textMuted,
                        lineHeight: '1.4',
                      }}
                    >
                      {description}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
          {/* Not a product, so not a tile — see .mega-menu-resource. The docs
              live in the webapp, so this is a plain anchor the browser follows
              rather than a router push. */}
          <a className="mega-menu-resource" href="/developers/api">
            <BookOpen size={16} weight="duotone" color={MUTED} />
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 12.5,
                fontWeight: 600,
                color: marketing.textMuted,
              }}
            >
              {t('megaMenu.apiDocs.short', { defaultValue: 'Read the API documentation' })}
            </span>
            <span style={{ color: marketing.textMuted, fontSize: 12.5 }}>&#x2192;</span>
          </a>
        </div>
      </div>
    </div>
  );
};
